import { useState, useEffect, useRef, useCallback } from "react";
import { useMode } from "@/context/ModeContext";
import axios from "axios";
import { API } from "@/config";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, Info, ChevronsUpDown, X, ChevronDown, ChevronRight, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { PhoneInput } from "@/components/PhoneInput";
import { syncService } from "../services/SyncService";
import { useUserRole } from "@/hooks/useUserRole";
import { COUNTRY_CODES } from "../constants/common";
import { useDoctorSearch } from "../hooks/useDoctorSearch";
import { toPaise } from "../utils/currency";
import PropTypes from 'prop-types';
import { ReticleSignal, signal } from "@/reticle";

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

/**
 * Reusable Add Patient dialog with collapsible referral doctor and fee arrangement sections.
 * The fee section supports selecting an existing clinic (local or from backend) or creating
 * a new stub clinic (with backend dedup check) — matching the full FeeAgreementManager UX.
 * Used from both Patients page and Appointments page.
 */
export function AddPatientDialog({ open, onOpenChange, token, onPatientCreated }) {
    const { isReceptionistOnly, selectedRole } = useUserRole();
    const { activeMode } = useMode();
    const doctorId = localStorage.getItem('user_id');

    // Patient form data
    const [formData, setFormData] = useState({
        name: "", phone: "", email: "", age: "", gender: "male", notes: "",
        referral_doctor_id: "", referral_doctor_name: "", referral_doctor_phone: "",
        date_of_birth: "", address: "", ageInputMode: "age"
    });

    // Fee arrangement form data (extended with stub-clinic fields)
    const [feeData, setFeeData] = useState({
        clinic_id: "", rule_type: "percent", fee_value: "",
        clinic_name: "", clinic_phone: "", clinic_address: ""
    });

    // Collapsible section states
    const [showReferralSection, setShowReferralSection] = useState(false);
    const [showFeeSection, setShowFeeSection] = useState(false);

    // Phone search states
    const [isSearching, setIsSearching] = useState(false);
    const [isGlobalPatient, setIsGlobalPatient] = useState(false);
    const [phoneError, setPhoneError] = useState("");
    const [isPhoneSearchComplete, setIsPhoneSearchComplete] = useState(false);
    const searchTimeoutRef = useRef(null);

    // Referral doctor states
    const [openReferral, setOpenReferral] = useState(false);

    // Clinic combobox state for fee arrangement
    const [openClinicSelect, setOpenClinicSelect] = useState(false);

    // Backend dropdown search for fee clinic
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');
    const [dropdownSearchResults, setDropdownSearchResults] = useState([]);
    const [dropdownSearching, setDropdownSearching] = useState(false);
    const dropdownSearchTimer = useRef(null);

    // New stub-clinic search state machine: idle | searching | found | not_found
    const [newClinicSearchState, setNewClinicSearchState] = useState('idle');
    const [newClinicSearchResults, setNewClinicSearchResults] = useState([]);
    const newClinicSearchTimer = useRef(null);

    // Auto-selected conflict clinic
    const [clinicConflict, setClinicConflict] = useState(null);

    // Data from Dexie
    const clinics = useLiveQuery(() => db.clinics.toArray()) || [];

    const referralDoctors = useLiveQuery(async () => {
        if (!doctorId) return [];
        const createdByMe = await db.users.where('created_by_user_id').equals(doctorId).toArray();
        const uniqueIds = new Set(createdByMe.map(u => u.id));
        const myLinks = await db.patient_doctor_links.where('user_id').equals(doctorId).toArray();
        const myPatientIds = myLinks.map(l => l.patient_id);
        const myPatients = await db.patients.bulkGet(myPatientIds);
        myPatients.forEach(p => { if (p && p.referral_doctor_id) uniqueIds.add(p.referral_doctor_id); });
        const myClinics = await db.clinic_members.where('user_id').equals(doctorId).toArray();
        const myClinicIds = myClinics.map(m => m.clinic_id);
        if (myClinicIds.length > 0) {
            const clinicMembers = await db.clinic_members.where('clinic_id').anyOf(myClinicIds).toArray();
            clinicMembers.forEach(m => { if (m.user_id !== doctorId) uniqueIds.add(m.user_id); });
        }
        const allReferralUsers = await db.users.bulkGet(Array.from(uniqueIds));
        return allReferralUsers
            .filter(u => u && u.id !== doctorId)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [doctorId]) || [];

    // Resolve clinics for fee arrangement (clinics the user is a member of)
    const userClinics = useLiveQuery(async () => {
        if (!doctorId) return [];
        const memberships = await db.clinic_members.where('user_id').equals(doctorId)
            .filter(m => m.is_active !== false).toArray();
        const clinicIds = memberships.map(m => m.clinic_id);
        return db.clinics.where('id').anyOf(clinicIds).toArray();
    }, [doctorId]) || [];

    // Referral doctor search hook
    const {
        isSearching: isReferralSearching,
        searchComplete: referralSearchComplete,
        searchResults: referralSearchResults,
        searchDoctorByPhone: triggerReferralSearch,
        setSearchComplete: setReferralSearchComplete,
        getLocalNumber
    } = useDoctorSearch(token);

    // Handle referral search results
    useEffect(() => {
        if (referralSearchResults) {
            if (referralSearchResults.found) {
                setFormData(prev => ({
                    ...prev,
                    referral_doctor_name: referralSearchResults.doctor.name,
                    referral_doctor_id: referralSearchResults.source === 'local' ? referralSearchResults.doctor.id : ""
                }));
            } else {
                setFormData(prev => ({ ...prev, referral_doctor_name: "" }));
            }
        }
    }, [referralSearchResults]);

    // Debounced referral phone search
    const referralSearchTimeoutRef = useRef(null);
    const lastSearchedPhoneRef = useRef(null);

    useEffect(() => {
        const phone = formData.referral_doctor_phone;
        if (!phone) {
            if (lastSearchedPhoneRef.current) {
                setReferralSearchComplete(false);
                lastSearchedPhoneRef.current = null;
            }
            return;
        }
        const localNumber = getLocalNumber(phone);
        if (localNumber.length === 10) {
            if (lastSearchedPhoneRef.current !== phone) {
                if (referralSearchTimeoutRef.current) clearTimeout(referralSearchTimeoutRef.current);
                setReferralSearchComplete(false);
                if (formData.referral_doctor_id) {
                    setFormData(prev => ({ ...prev, referral_doctor_id: "", referral_doctor_name: "" }));
                }
                referralSearchTimeoutRef.current = setTimeout(() => {
                    lastSearchedPhoneRef.current = phone;
                    triggerReferralSearch(phone);
                }, 500);
            }
        }
        return () => { if (referralSearchTimeoutRef.current) clearTimeout(referralSearchTimeoutRef.current); };
    }, [formData.referral_doctor_phone, formData.referral_doctor_id, getLocalNumber, setReferralSearchComplete, triggerReferralSearch]);

    // Patient phone search
    const handlePhoneSearch = useCallback(async () => {
        if (!formData.phone || formData.phone.length < 10) {
            setPhoneError("Please enter a valid phone number");
            return;
        }
        setPhoneError("");
        setIsSearching(true);
        try {
            const localPatient = await db.patients.where('phone').equals(formData.phone).first();
            if (localPatient && !localPatient.deleted) {
                setPhoneError("Patient already exists in your list");
                setIsSearching(false);
                setIsPhoneSearchComplete(true);
                return;
            }
            try {
                const response = await axios.get(`${API}/patients/search?q=${encodeURIComponent(formData.phone)}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const globalPatient = response.data.find(p => p.phone === formData.phone);
                if (globalPatient) {
                    setFormData(prev => ({
                        ...prev, name: globalPatient.name, email: globalPatient.email || "",
                        age: globalPatient.age || "", gender: globalPatient.gender,
                        date_of_birth: globalPatient.date_of_birth || "",
                        address: globalPatient.address || "",
                        ageInputMode: globalPatient.date_of_birth ? "dob" : "age"
                    }));
                    setIsGlobalPatient(true);
                    toast.success("Patient found in global database!");
                } else {
                    setIsGlobalPatient(false);
                    toast.info("New patient. Please fill in details.");
                }
            } catch (err) {
                console.error("Global search failed", err);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsSearching(false);
            setIsPhoneSearchComplete(true);
        }
    }, [formData.phone, token]);

    // Auto-trigger phone search at 10 digits
    useEffect(() => {
        const phone = formData.phone;
        if (!phone) { setIsPhoneSearchComplete(false); return; }
        let localNumber = phone;
        const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.length - a.length);
        for (const code of sortedCodes) {
            if (phone.startsWith(code)) { localNumber = phone.slice(code.length); break; }
        }
        setIsPhoneSearchComplete(false);
        setPhoneError("");
        if (isGlobalPatient) setIsGlobalPatient(false);
        if (localNumber.length === 10) {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = setTimeout(() => { handlePhoneSearch(); }, 500);
        }
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [formData.phone, handlePhoneSearch]);

    // ─── Stub clinic helpers (ported from FeeAgreementManager) ────────────────

    const getAuthConfig = useCallback(() => ({
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    // Save a backend clinic locally and ensure user is a member
    const saveClinicLocally = useCallback(async (clinic) => {
        const existing = await db.clinics.get(clinic.id);
        if (!existing) {
            await db.clinics.add({ ...clinic, _synced: true });
        }
        const memberId = `mem_${clinic.id}_${doctorId}`;
        const existingMember = await db.clinic_members.get(memberId);
        if (!existingMember) {
            const newMember = {
                id: memberId,
                clinic_id: clinic.id,
                user_id: doctorId,
                role: 'doctor',
                is_active: true,
                created_at: new Date().toISOString(),
                _synced: false
            };
            await db.clinic_members.add(newMember);
            await syncService.addToQueue('create', 'clinic_member', newMember);
        }
    }, [doctorId]);

    // Extract last 10 digits from PhoneInput value
    const extractLocalPhone = (val) => {
        if (!val) return '';
        const digits = val.replace(/\D/g, '');
        return digits.length >= 10 ? digits.slice(-10) : digits;
    };

    // Debounced backend search for clinic dropdown
    const handleDropdownSearch = useCallback((term) => {
        setDropdownSearchTerm(term);
        if (dropdownSearchTimer.current) clearTimeout(dropdownSearchTimer.current);
        if (!term || term.length < 2) { setDropdownSearchResults([]); return; }
        dropdownSearchTimer.current = setTimeout(async () => {
            setDropdownSearching(true);
            try {
                const res = await axios.get(
                    `${API_URL}/api/clinics/search?name=${encodeURIComponent(term)}&is_placeholder=true`,
                    getAuthConfig()
                );
                const localIds = new Set(clinics.map(c => c.id));
                setDropdownSearchResults(res.data.filter(c => !localIds.has(c.id)));
            } catch (e) {
                console.error('Clinic search failed:', e);
            } finally {
                setDropdownSearching(false);
            }
        }, 400);
    }, [clinics, getAuthConfig]);

    // Debounced backend dedup check for new stub clinic form
    const triggerNewClinicBackendSearch = useCallback((name, rawPhone) => {
        if (newClinicSearchTimer.current) clearTimeout(newClinicSearchTimer.current);
        setNewClinicSearchResults([]);
        if (!name || name.length < 2) {
            setNewClinicSearchState('idle');
            return;
        }
        const localPhone = extractLocalPhone(rawPhone);
        const hasFullPhone = localPhone.length === 10;
        setNewClinicSearchState('searching');
        newClinicSearchTimer.current = setTimeout(async () => {
            try {
                const params = new URLSearchParams({ name, is_placeholder: 'true' });
                if (hasFullPhone) params.set('phone', localPhone);
                const res = await axios.get(`${API_URL}/api/clinics/search?${params}`, getAuthConfig());
                const results = res.data;
                setNewClinicSearchResults(results);
                if (results.length === 0) {
                    setNewClinicSearchState('not_found');
                } else {
                    const exact = hasFullPhone ? results.find(
                        c => c.name?.toLowerCase() === name.trim().toLowerCase() && c.phone === localPhone
                    ) : null;
                    if (exact) {
                        await saveClinicLocally(exact);
                        setClinicConflict(exact);
                        setFeeData(prev => ({ ...prev, clinic_id: exact.id, clinic_name: '', clinic_phone: '', clinic_address: '' }));
                        setNewClinicSearchResults([]);
                        setNewClinicSearchState('idle');
                    } else {
                        setNewClinicSearchState('found');
                    }
                }
            } catch (e) {
                console.error('New clinic backend search failed:', e);
                setNewClinicSearchState('idle');
            }
        }, 500);
    }, [saveClinicLocally, getAuthConfig]);

    // Cleanup timers on unmount
    useEffect(() => () => {
        if (dropdownSearchTimer.current) clearTimeout(dropdownSearchTimer.current);
        if (newClinicSearchTimer.current) clearTimeout(newClinicSearchTimer.current);
    }, []);

    // Local conflict check: same name + phone already in Dexie
    const checkClinicConflict = (name, rawPhone) => {
        if (!name) return null;
        const localPhone = extractLocalPhone(rawPhone);
        if (localPhone.length !== 10) return null;
        const nameLower = name.trim().toLowerCase();
        return clinics.find(
            c => c.name?.toLowerCase() === nameLower && c.phone === localPhone
        ) || null;
    };

    // Whether the fee Save (via patient submit) is enabled for the fee section
    const isFeeValid = (() => {
        if (!showFeeSection) return true; // fee section is optional
        if (!feeData.fee_value) return true; // empty fee_value means user hasn't filled it → still OK to submit patient
        const hasValue = feeData.fee_value !== '' && Number(feeData.fee_value) >= 0;
        if (!hasValue) return false;
        if (feeData.clinic_id) return true;
        // new stub clinic path
        if (!feeData.clinic_id && feeData.clinic_name && extractLocalPhone(feeData.clinic_phone).length === 10) {
            return newClinicSearchState === 'not_found';
        }
        return false; // fee_value filled but no clinic chosen → block submit
    })();

    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            setFormData({
                name: "", phone: "", email: "", age: "", gender: "male", notes: "",
                referral_doctor_id: "", referral_doctor_name: "", referral_doctor_phone: "",
                date_of_birth: "", address: "", ageInputMode: "age"
            });
            setFeeData({ clinic_id: "", rule_type: "percent", fee_value: "", clinic_name: "", clinic_phone: "", clinic_address: "" });
            setShowReferralSection(false);
            setShowFeeSection(false);
            setIsGlobalPatient(false);
            setPhoneError("");
            setIsPhoneSearchComplete(false);
            setDropdownSearchResults([]);
            setDropdownSearchTerm('');
            setNewClinicSearchState('idle');
            setNewClinicSearchResults([]);
            setClinicConflict(null);
        }
    }, [open]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (phoneError) return;

        try {
            // Resolve treated_by_doctor_id
            let treatedBy = doctorId;
            let clinicId = null;

            if (activeMode && activeMode !== 'personal' && activeMode !== 'everything') {
                let clinic = clinics.find(c => c.id === activeMode);
                if (!clinic) clinic = await db.clinics.get(activeMode);
                if (clinic) {
                    treatedBy = clinic.owner_doctor_id;
                    clinicId = clinic.id;
                }
            }

            // Resolve referral doctor
            let finalReferralId = formData.referral_doctor_id;
            if (!finalReferralId && formData.referral_doctor_name) {
                const existingDoctor = await db.users.where('phone').equals(formData.referral_doctor_phone).first();
                if (existingDoctor) {
                    finalReferralId = existingDoctor.id;
                } else {
                    finalReferralId = crypto.randomUUID();
                    const newDoctor = {
                        id: finalReferralId,
                        name: formData.referral_doctor_name,
                        phone: formData.referral_doctor_phone,
                        email: `placeholder_${finalReferralId}@clinicnode.com`,
                        is_registered: false,
                        created_by_user_id: doctorId,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                        _synced: false
                    };
                    await db.users.add(newDoctor);
                    await syncService.addToQueue('create', 'referral-doctor', {
                        id: finalReferralId, name: formData.referral_doctor_name, phone: formData.referral_doctor_phone
                    });
                }
            }

            // Create patient
            const nowIso = new Date().toISOString();
            const patientData = {
                name: formData.name,
                phone: formData.phone,
                email: formData.email || null,
                gender: formData.gender,
                notes: formData.notes || "",
                address: formData.address || null
            };

            if (formData.ageInputMode === "age" && formData.age) {
                patientData.age = parseInt(formData.age);
                patientData.ageRecordedOn = nowIso;
                patientData.date_of_birth = null;
            } else if (formData.ageInputMode === "dob" && formData.date_of_birth) {
                patientData.age = null;
                patientData.ageRecordedOn = null;
                patientData.date_of_birth = formData.date_of_birth;
            } else {
                patientData.age = null;
                patientData.ageRecordedOn = null;
                patientData.date_of_birth = null;
            }

            const newPatient = {
                id: crypto.randomUUID(),
                treated_by_doctor_id: treatedBy,
                clinic_id: clinicId,
                ...patientData,
                referral_doctor_id: finalReferralId,
                created_at: nowIso,
                updated_at: nowIso,
                _synced: false
            };

            await db.patients.add(newPatient);
            await syncService.addToQueue('create', 'patient', newPatient);

            // Create local patient-doctor link
            try {
                await db.patient_doctor_links.add({
                    id: crypto.randomUUID(),
                    patient_id: newPatient.id,
                    user_id: treatedBy,
                    clinic_id: clinicId || null,
                    _synced: true
                });
            } catch (e) {
                console.warn("Could not create local link", e);
            }

            // Create fee agreement if fee section was filled
            if (showFeeSection && feeData.fee_value) {
                let finalFeeClinicId = feeData.clinic_id;

                // Create stub clinic if user filled in a new one
                if (!finalFeeClinicId && feeData.clinic_name) {
                    if (!feeData.clinic_phone) {
                        toast.error("Phone number is required when creating a new clinic.");
                        return;
                    }
                    finalFeeClinicId = crypto.randomUUID();
                    const newClinic = {
                        id: finalFeeClinicId,
                        name: feeData.clinic_name,
                        phone: extractLocalPhone(feeData.clinic_phone),
                        address: feeData.clinic_address || "",
                        email: "",
                        owner_doctor_id: doctorId,
                        is_placeholder: true,
                        created_by_user_id: doctorId,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                        _synced: false
                    };
                    await db.clinics.add(newClinic);
                    await syncService.addToQueue('create', 'clinic', newClinic);

                    // Optimistically add membership
                    const newMember = {
                        id: `mem_${finalFeeClinicId}_${doctorId}`,
                        clinic_id: finalFeeClinicId,
                        user_id: doctorId,
                        role: 'doctor',
                        is_active: true,
                        created_at: new Date().toISOString(),
                        _synced: false
                    };
                    await db.clinic_members.add(newMember);
                    await syncService.addToQueue('create', 'clinic_member', newMember);
                }

                if (finalFeeClinicId) {
                    const feeValue = parseFloat(feeData.fee_value);
                    const finalFeeValue = feeData.rule_type === 'flat' ? toPaise(feeValue) : feeValue;

                    const newRule = {
                        id: crypto.randomUUID(),
                        name: null,
                        rule_type: feeData.rule_type,
                        fee_value: finalFeeValue,
                        patient_id: newPatient.id,
                        clinic_id: finalFeeClinicId,
                        user_id: doctorId,
                        status: "active",
                        arrangement_type: "facility_fee",
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                        _synced: false
                    };
                    await db.fee_agreements.add(newRule);
                    await syncService.addToQueue('create', 'fee_agreement', newRule);
                }
            }

            signal(ReticleSignal.PATIENT_CREATED, {
                patientId: newPatient.id,
                actorId: doctorId,
                clinicId: newPatient.clinic_id,
                role: selectedRole,
            });

            toast.success(isGlobalPatient ? "Patient added to your list!" : "Patient added successfully!");
            onOpenChange(false);

            if (onPatientCreated) {
                onPatientCreated(newPatient);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to add patient");
        }
    };

    const disableFields = !isPhoneSearchComplete || !!phoneError;
    const disableFieldsOrGlobal = disableFields || isGlobalPatient;
    const isSubmitDisabled = !isPhoneSearchComplete || !!phoneError || !isFeeValid;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl"
                data-testid="add-patient-dialog"
                style={{
                    maxHeight: '90vh', overflow: 'hidden',
                    display: 'flex', flexDirection: 'column'
                }}
            >
                <DialogHeader>
                    <DialogTitle>Add New Patient</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} style={{ overflow: 'auto', flex: 1, paddingBottom: '1rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        {/* Phone Number */}
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Label htmlFor="phone">Phone Number</Label>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <PhoneInput
                                    id="phone"
                                    data-testid="patient-phone-input"
                                    value={formData.phone}
                                    onChange={(val) => setFormData({ ...formData, phone: val })}
                                    placeholder="Enter 10-digit number"
                                    required
                                    style={{ flex: 1 }}
                                    showError={true}
                                />
                                {isSearching && <div className="loader small" style={{ marginLeft: '0.5rem' }} />}
                                {isPhoneSearchComplete && !phoneError && <span style={{ color: '#22c55e', marginLeft: '0.5rem' }}>✓</span>}
                                {'contacts' in navigator && (
                                    <Button type="button" variant="outline" size="sm"
                                        onClick={async () => {
                                            try {
                                                const contacts = await navigator.contacts.select(['tel'], { multiple: false });
                                                if (contacts?.[0]?.tel?.[0]) setFormData({ ...formData, phone: contacts[0].tel[0] });
                                            } catch (e) { console.error(e); }
                                        }}
                                        style={{ minWidth: '40px', padding: '0 0.5rem' }}>📇</Button>
                                )}
                            </div>
                            {phoneError && (
                                <div style={{
                                    color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem',
                                    padding: '0.5rem 0.75rem', backgroundColor: '#fef2f2',
                                    borderRadius: '6px', border: '1px solid #fecaca'
                                }}>⚠️ {phoneError}</div>
                            )}
                            {phoneError && phoneError.includes("already exists") && (
                                <div style={{
                                    padding: "0.5rem 0.75rem", background: "#f0f9ff", borderRadius: "6px",
                                    border: "1px solid #bae6fd", display: "flex", alignItems: "flex-start",
                                    gap: "0.5rem", marginTop: "0.5rem"
                                }}>
                                    <Info size={14} style={{ color: "#0284c7", marginTop: "2px", flexShrink: 0 }} />
                                    <p style={{ fontSize: "0.75rem", color: "#0369a1", margin: 0, lineHeight: 1.4 }}>
                                        Can&apos;t see the patient in your list? Check if you&apos;ve selected the correct <strong>mode</strong> (Personal/Clinic) from the sidebar.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Core fields */}
                        <div>
                            <Label htmlFor="email">Email (Optional)</Label>
                            <Input id="email" type="email" value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="patient@example.com"
                                disabled={disableFieldsOrGlobal}
                                style={disableFieldsOrGlobal ? { backgroundColor: '#f1f5f9' } : {}} />
                        </div>
                        <div>
                            <Label htmlFor="name">Patient Name</Label>
                            <Input id="name" data-testid="patient-name-input" value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required disabled={disableFieldsOrGlobal}
                                style={disableFieldsOrGlobal ? { backgroundColor: '#f1f5f9' } : {}} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <Label>Age / Date of Birth (Optional)</Label>
                            <div style={{ display: 'flex', background: '#f1f5f9', padding: '0.3rem', borderRadius: '8px', width: '100%', border: '1px solid #e2e8f0' }}>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, ageInputMode: 'age', date_of_birth: '' })}
                                    style={{
                                        flex: 1,
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        background: formData.ageInputMode === 'age' ? 'white' : 'transparent',
                                        boxShadow: formData.ageInputMode === 'age' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: formData.ageInputMode === 'age' ? '#0ea5e9' : '#64748b',
                                        transition: 'all 0.15s'
                                    }}
                                    disabled={disableFieldsOrGlobal}
                                >
                                    Age
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, ageInputMode: 'dob', age: '' })}
                                    style={{
                                        flex: 1,
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        background: formData.ageInputMode === 'dob' ? 'white' : 'transparent',
                                        boxShadow: formData.ageInputMode === 'dob' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: formData.ageInputMode === 'dob' ? '#0ea5e9' : '#64748b',
                                        transition: 'all 0.15s'
                                    }}
                                    disabled={disableFieldsOrGlobal}
                                >
                                    Date of Birth
                                </button>
                            </div>
                            
                            {formData.ageInputMode === 'age' ? (
                                <Input
                                    id="age"
                                    data-testid="patient-age-input"
                                    type="number"
                                    value={formData.age}
                                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                                    placeholder="Enter age in years"
                                    disabled={disableFieldsOrGlobal}
                                    style={disableFieldsOrGlobal ? { backgroundColor: '#f1f5f9' } : {}}
                                />
                            ) : (
                                <Input
                                    id="date_of_birth"
                                    data-testid="patient-dob-input"
                                    type="date"
                                    value={formData.date_of_birth}
                                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                                    disabled={disableFieldsOrGlobal}
                                    style={disableFieldsOrGlobal ? { backgroundColor: '#f1f5f9' } : {}}
                                />
                            )}
                        </div>
                        <div>
                            <Label htmlFor="gender">Gender</Label>
                            <Select value={formData.gender}
                                onValueChange={(value) => setFormData({ ...formData, gender: value })}
                                disabled={disableFieldsOrGlobal}>
                                <SelectTrigger data-testid="patient-gender-select"
                                    style={disableFieldsOrGlobal ? { backgroundColor: '#f1f5f9' } : {}}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="male">Male</SelectItem>
                                    <SelectItem value="female">Female</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Label htmlFor="address">Address (Optional)</Label>
                            <Input id="address" data-testid="patient-address-input" value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                placeholder="Enter patient address"
                                disabled={disableFields}
                                style={disableFields ? { backgroundColor: '#f1f5f9' } : {}} />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Label htmlFor="notes">Notes</Label>
                            <Input id="notes" data-testid="patient-notes-input" value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                disabled={disableFields}
                                style={disableFields ? { backgroundColor: '#f1f5f9' } : {}} />
                        </div>

                        {/* Collapsible Referral Doctor Section */}
                        {!isReceptionistOnly && (
                            <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                                <button
                                    type="button"
                                    data-testid="referral-section-toggle"
                                    onClick={() => setShowReferralSection(!showReferralSection)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                                        width: '100%', padding: '0.75rem', background: '#f8fafc',
                                        border: '1px solid #e2e8f0', borderRadius: '8px',
                                        cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600',
                                        color: '#334155', transition: 'background 0.15s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                    onMouseOut={(e) => e.currentTarget.style.background = '#f8fafc'}
                                >
                                    {showReferralSection ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    Referral Information (Optional)
                                </button>

                                {showReferralSection && (
                                    <div style={{
                                        padding: '1rem', border: '1px solid #e2e8f0',
                                        borderTop: 'none', borderRadius: '0 0 8px 8px', background: '#fafbfc'
                                    }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
                                            <div>
                                                <Label htmlFor="referral-doctor">Select Existing Referral Doctor</Label>
                                                <Popover open={openReferral} onOpenChange={setOpenReferral}>
                                                    <PopoverTrigger asChild>
                                                        <Button variant="outline" role="combobox" aria-expanded={openReferral}
                                                            className="w-full justify-between" data-testid="referral-doctor-select">
                                                            {formData.referral_doctor_id
                                                                ? referralDoctors.find((doc) => doc.id === formData.referral_doctor_id)?.name
                                                                : "Select doctor..."}
                                                            <div className="flex items-center gap-1 opacity-50">
                                                                {formData.referral_doctor_id && (
                                                                    <div role="button"
                                                                        onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, referral_doctor_id: "" }); }}
                                                                        className="hover:bg-slate-200 rounded p-0.5">
                                                                        <X className="h-3 w-3" />
                                                                    </div>
                                                                )}
                                                                <ChevronsUpDown className="h-4 w-4" />
                                                            </div>
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-[300px] p-0" align="start">
                                                        <Command>
                                                            <CommandInput placeholder="Search doctor..." />
                                                            <CommandList>
                                                                <CommandEmpty>No doctor found.</CommandEmpty>
                                                                <CommandGroup>
                                                                    {referralDoctors.map((doc) => (
                                                                        <CommandItem key={doc.id} value={doc.name}
                                                                            onSelect={() => {
                                                                                setFormData({ ...formData, referral_doctor_id: doc.id });
                                                                                setOpenReferral(false);
                                                                            }}>
                                                                            <Check className={cn("mr-2 h-4 w-4",
                                                                                formData.referral_doctor_id === doc.id ? "opacity-100" : "opacity-0")} />
                                                                            <div className="flex flex-col">
                                                                                <span>{doc.name}</span>
                                                                                <span className="text-xs text-muted-foreground">{doc.phone}</span>
                                                                            </div>
                                                                        </CommandItem>
                                                                    ))}
                                                                </CommandGroup>
                                                            </CommandList>
                                                        </Command>
                                                    </PopoverContent>
                                                </Popover>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', paddingBottom: '0.5rem' }}>
                                                OR
                                            </div>

                                            <div>
                                                <Label htmlFor="new-ref-phone">New Referral Doctor Phone</Label>
                                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                    <PhoneInput
                                                        id="new-ref-phone" data-testid="new-referral-phone-input"
                                                        value={formData.referral_doctor_phone}
                                                        onChange={(val) => setFormData({ ...formData, referral_doctor_phone: val, referral_doctor_id: "" })}
                                                        disabled={!!formData.referral_doctor_id && referralDoctors.some(d => d.id === formData.referral_doctor_id)}
                                                        style={{ flex: 1 }}
                                                    />
                                                    {isReferralSearching && <div className="loader small" />}
                                                    {'contacts' in navigator && !formData.referral_doctor_id && (
                                                        <Button type="button" variant="outline" size="sm"
                                                            onClick={async () => {
                                                                try {
                                                                    const contacts = await navigator.contacts.select(['tel'], { multiple: false });
                                                                    if (contacts?.[0]?.tel?.[0]) setFormData({ ...formData, referral_doctor_phone: contacts[0].tel[0] });
                                                                } catch { /* ignore */ }
                                                            }}
                                                            style={{ minWidth: '40px', padding: '0 0.5rem' }}>📇</Button>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <Label htmlFor="new-ref-name">New Referral Doctor Name</Label>
                                                <Input id="new-ref-name" data-testid="new-referral-name-input"
                                                    value={formData.referral_doctor_name}
                                                    onChange={(e) => setFormData({ ...formData, referral_doctor_name: e.target.value })}
                                                    disabled={!!formData.referral_doctor_id || !referralSearchComplete}
                                                    placeholder={!referralSearchComplete && formData.referral_doctor_phone ? "Enter phone to search..." : ""} />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Collapsible Fee Arrangement Section */}
                        {!isReceptionistOnly && (
                            <div style={{ gridColumn: '1 / -1' }}>
                                <button
                                    type="button"
                                    data-testid="fee-section-toggle"
                                    onClick={() => setShowFeeSection(!showFeeSection)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                                        width: '100%', padding: '0.75rem', background: '#f8fafc',
                                        border: '1px solid #e2e8f0', borderRadius: '8px',
                                        cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600',
                                        color: '#334155', transition: 'background 0.15s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                    onMouseOut={(e) => e.currentTarget.style.background = '#f8fafc'}
                                >
                                    {showFeeSection ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    Fee Arrangement (Optional)
                                </button>

                                {showFeeSection && (
                                    <div style={{
                                        padding: '1rem', border: '1px solid #e2e8f0',
                                        borderTop: 'none', borderRadius: '0 0 8px 8px', background: '#fafbfc'
                                    }}>
                                        {/* ── Clinic selector section ── */}
                                        <div className="space-y-3 border p-3 rounded-md bg-slate-50 mb-3">
                                            <h4 style={{ fontSize: '0.8125rem', fontWeight: '600', margin: 0 }}>Select or Create Clinic</h4>

                                            {/* Clinic combobox with backend search */}
                                            <Popover open={openClinicSelect} onOpenChange={setOpenClinicSelect}>
                                                <PopoverTrigger asChild>
                                                    <Button variant="outline" role="combobox" aria-expanded={openClinicSelect}
                                                        className="w-full justify-between" data-testid="fee-clinic-select">
                                                        {feeData.clinic_id
                                                            ? (userClinics.find(c => c.id === feeData.clinic_id)?.name
                                                                || clinics.find(c => c.id === feeData.clinic_id)?.name
                                                                || "Selected")
                                                            : "Select a clinic..."}
                                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-full p-0">
                                                    <Command filter={(value, search) => {
                                                        if (value.toLowerCase().includes(search.toLowerCase())) return 1;
                                                        return 0;
                                                    }}>
                                                        <CommandInput
                                                            placeholder="Search clinics..."
                                                            onValueChange={handleDropdownSearch}
                                                        />
                                                        <CommandList>
                                                            {dropdownSearching && (
                                                                <div className="flex items-center justify-center py-2 text-xs text-muted-foreground gap-1">
                                                                    <Loader2 className="h-3 w-3 animate-spin" /> Searching...
                                                                </div>
                                                            )}
                                                            <CommandEmpty>{dropdownSearchTerm.length >= 2 && !dropdownSearching ? 'No clinics found.' : 'Type to search...'}</CommandEmpty>
                                                            <CommandGroup heading="Local">
                                                                {userClinics.map((clinic) => (
                                                                    <CommandItem
                                                                        key={clinic.id}
                                                                        value={clinic.name}
                                                                        onSelect={() => {
                                                                            setFeeData({ ...feeData, clinic_id: clinic.id, clinic_name: "", clinic_phone: "", clinic_address: "" });
                                                                            setClinicConflict(null);
                                                                            setOpenClinicSelect(false);
                                                                        }}
                                                                    >
                                                                        <Check className={cn("mr-2 h-4 w-4", feeData.clinic_id === clinic.id ? "opacity-100" : "opacity-0")} />
                                                                        {clinic.name} {clinic.is_placeholder && "(Stub)"}
                                                                    </CommandItem>
                                                                ))}
                                                            </CommandGroup>
                                                            {dropdownSearchResults.length > 0 && (
                                                                <CommandGroup heading="From Server">
                                                                    {dropdownSearchResults.map((clinic) => (
                                                                        <CommandItem
                                                                            key={clinic.id}
                                                                            value={clinic.name}
                                                                            onSelect={async () => {
                                                                                await saveClinicLocally(clinic);
                                                                                setFeeData({ ...feeData, clinic_id: clinic.id, clinic_name: "", clinic_phone: "", clinic_address: "" });
                                                                                setClinicConflict(null);
                                                                                setDropdownSearchResults([]);
                                                                                setOpenClinicSelect(false);
                                                                            }}
                                                                        >
                                                                            <Check className={cn("mr-2 h-4 w-4", feeData.clinic_id === clinic.id ? "opacity-100" : "opacity-0")} />
                                                                            {clinic.name} {clinic.phone && <span className="text-xs text-muted-foreground ml-1">{clinic.phone}</span>}
                                                                        </CommandItem>
                                                                    ))}
                                                                </CommandGroup>
                                                            )}
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>

                                            {/* Clear selection button */}
                                            {feeData.clinic_id && (
                                                <div className="space-y-1">
                                                    {clinicConflict && (
                                                        <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                                                            <Check className="h-3 w-3 shrink-0" />
                                                            Existing clinic &quot;{clinicConflict.name}&quot; auto-selected (name + phone matched)
                                                        </div>
                                                    )}
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="mt-1 h-auto p-0 text-xs text-muted-foreground hover:text-destructive"
                                                        onClick={() => { setFeeData(prev => ({ ...prev, clinic_id: '' })); setClinicConflict(null); }}
                                                        data-testid="fee-clinic-clear"
                                                    >
                                                        <X className="mr-1 h-3 w-3 inline" /> Clear Selection
                                                    </Button>
                                                </div>
                                            )}

                                            {/* New stub clinic form (shown when no clinic is selected) */}
                                            {!feeData.clinic_id && (
                                                <>
                                                    <div className="text-center text-xs text-muted-foreground my-2">OR ADD NEW STUB CLINIC</div>
                                                    <div className="space-y-3">
                                                        <div>
                                                            <Label>Clinic Name</Label>
                                                            <Input
                                                                data-testid="new-clinic-name-input"
                                                                placeholder="Enter clinic name"
                                                                value={feeData.clinic_name}
                                                                onChange={e => {
                                                                    const newName = e.target.value;
                                                                    const conflict = checkClinicConflict(newName, feeData.clinic_phone);
                                                                    setClinicConflict(conflict);
                                                                    if (conflict) {
                                                                        setFeeData(prev => ({ ...prev, clinic_name: newName, clinic_id: conflict.id, clinic_phone: '', clinic_address: '' }));
                                                                    } else {
                                                                        setFeeData(prev => ({ ...prev, clinic_name: newName }));
                                                                        triggerNewClinicBackendSearch(newName, feeData.clinic_phone);
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>Phone *</Label>
                                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                                <PhoneInput
                                                                    id="new-clinic-phone"
                                                                    data-testid="new-clinic-phone-input"
                                                                    placeholder="Clinic phone"
                                                                    value={feeData.clinic_phone}
                                                                    onChange={val => {
                                                                        const conflict = checkClinicConflict(feeData.clinic_name, val);
                                                                        setClinicConflict(conflict);
                                                                        if (conflict) {
                                                                            setFeeData(prev => ({ ...prev, clinic_phone: val, clinic_id: conflict.id, clinic_name: '', clinic_address: '' }));
                                                                        } else {
                                                                            setFeeData(prev => ({ ...prev, clinic_phone: val }));
                                                                            triggerNewClinicBackendSearch(feeData.clinic_name, val);
                                                                        }
                                                                    }}
                                                                    style={{ flex: 1 }}
                                                                />
                                                                {'contacts' in navigator && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={async () => {
                                                                            try {
                                                                                const contacts = await navigator.contacts.select(['tel', 'name'], { multiple: false });
                                                                                if (contacts?.[0]?.tel?.[0]) {
                                                                                    let phone = contacts[0].tel[0].replace(/\D/g, '');
                                                                                    if (phone.length > 10) phone = phone.slice(-10);
                                                                                    if (phone.length !== 10) { toast.error('Phone must be 10 digits.'); return; }
                                                                                    const newName = (!feeData.clinic_name && contacts[0].name?.[0]) ? contacts[0].name[0] : feeData.clinic_name;
                                                                                    setFeeData(prev => ({ ...prev, clinic_phone: phone, clinic_name: newName }));
                                                                                }
                                                                            } catch (error) { console.error(error); }
                                                                        }}
                                                                        style={{ minWidth: '40px', padding: '0 0.5rem' }}
                                                                    >📇</Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <Label>Address (Optional)</Label>
                                                            <Input
                                                                data-testid="new-clinic-address-input"
                                                                placeholder="Clinic address"
                                                                value={feeData.clinic_address}
                                                                onChange={e => setFeeData(prev => ({ ...prev, clinic_address: e.target.value }))}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Backend dedup feedback */}
                                                    {newClinicSearchState === 'searching' && (
                                                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                                                            <Loader2 className="h-3 w-3 animate-spin" /> Checking server for duplicates...
                                                        </div>
                                                    )}
                                                    {newClinicSearchState === 'not_found' && (
                                                        <div className="flex items-center gap-1 text-xs text-green-700 mt-1" data-testid="new-clinic-ok-badge">
                                                            <Check className="h-3 w-3" /> No existing clinic found — you can save to create a new one.
                                                        </div>
                                                    )}
                                                    {newClinicSearchState === 'found' && newClinicSearchResults.length > 0 && (
                                                        <div className="border rounded-md bg-white shadow-sm mt-1" data-testid="new-clinic-conflict-list">
                                                            <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1 border-b flex items-center gap-1">
                                                                <AlertCircle className="h-3 w-3" /> Existing clinics found — select one or clear name/phone to create new:
                                                            </div>
                                                            {newClinicSearchResults.map(c => (
                                                                <button
                                                                    key={c.id}
                                                                    type="button"
                                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between items-center"
                                                                    onClick={async () => {
                                                                        await saveClinicLocally(c);
                                                                        setClinicConflict(c);
                                                                        setFeeData(prev => ({ ...prev, clinic_id: c.id, clinic_name: '', clinic_phone: '', clinic_address: '' }));
                                                                        setNewClinicSearchResults([]);
                                                                        setNewClinicSearchState('idle');
                                                                    }}
                                                                >
                                                                    <span className="font-medium">{c.name}</span>
                                                                    <span className="text-xs text-muted-foreground">{c.phone}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Rule type + value */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                                            <div>
                                                <Label>Type</Label>
                                                <Select value={feeData.rule_type}
                                                    onValueChange={(val) => setFeeData({ ...feeData, rule_type: val })}>
                                                    <SelectTrigger data-testid="fee-type-select">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="percent">Percentage (%)</SelectItem>
                                                        <SelectItem value="flat">Flat Amount (₹)</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div>
                                                <Label>Value</Label>
                                                <Input type="number" step="1" min="0" data-testid="fee-value-input"
                                                    value={feeData.fee_value}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val === '' || /^\d+$/.test(val)) setFeeData({ ...feeData, fee_value: val });
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === '.' || e.key === '-' || e.key === 'e') e.preventDefault();
                                                    }}
                                                    placeholder={feeData.rule_type === 'percent' ? 'e.g. 10' : 'Amount in ₹'} />
                                            </div>
                                        </div>
                                        {(feeData.clinic_id || (feeData.clinic_name && newClinicSearchState === 'not_found')) && feeData.fee_value && (
                                            <div className="flex items-center gap-1 text-xs text-green-700 mt-2">
                                                <Check className="h-3 w-3" />
                                                Fee arrangement will be created with the patient.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </form>

                <div style={{
                    display: 'flex', gap: '0.75rem', marginTop: '1rem', paddingTop: '1rem',
                    paddingBottom: '0.5rem', borderTop: '1px solid #e2e8f0', flexShrink: 0,
                    background: 'white', position: 'sticky', bottom: 0, zIndex: 10
                }}>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} style={{ flex: 1 }}>
                        Cancel
                    </Button>
                    <Button
                        data-testid="submit-patient-button"
                        type="submit"
                        disabled={isSubmitDisabled}
                        onClick={(e) => {
                            e.preventDefault();
                            const form = document.querySelector('[data-testid="add-patient-dialog"] form');
                            if (form) form.requestSubmit();
                        }}
                        style={{
                            background: isSubmitDisabled ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            flex: 1
                        }}
                    >
                        Add Patient
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

AddPatientDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onOpenChange: PropTypes.func.isRequired,
    token: PropTypes.string,
    onPatientCreated: PropTypes.func
};
